import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Info,
  Key,
  Loader2,
  Plus,
  RefreshCw,
  ShieldOff,
  Trash2
} from "lucide-react";
import SettingsSection from "./SettingsSection";
import { LoadingState } from "../ui/LoadingStates";
import { createApiKey, listApiKeys } from "../../services/apiKeyService";
import LegacyApiKeySlots from "./apiKeys/LegacyApiKeySlots";
import {
  API_KEY_LIST_ID,
  RevokeKeyDialog,
  RotateKeyDialog
} from "./apiKeys/ApiKeyDialogs";
import {
  DEFAULT_EXPIRY,
  ErrorBox,
  ExpiryField,
  NEVER_EXPIRES,
  NewKeyResult,
  StatusChip,
  buildKeyLookup,
  expiryToDays,
  expiryToIso,
  formatDateTime,
  isLive,
  keyLabel,
  lastUsedText,
  listMayBeStale,
  sameName
} from "./apiKeys/apiKeyParts";

const POLICY_ADDITIVE = "additive_v1";
const POLICY_REVOKE_SAME_NAME = "revoke_same_name";
const KNOWN_POLICIES = [POLICY_ADDITIVE, POLICY_REVOKE_SAME_NAME];
const NAME_SUGGESTIONS = ["production", "sandbox"];
const MAX_NAME_LENGTH = 100;

const asText = (value) =>
  value === null || value === undefined ? "" : String(value);

/* ------------------------------------------------------------------ */
/* Key list                                                            */
/* ------------------------------------------------------------------ */

function ExpiryCell({ item }) {
  if (!item.expires_at) return <span>Never</span>;
  if (item.status === "retiring") {
    return <span>Retires {formatDateTime(item.expires_at)}</span>;
  }
  return <span>{formatDateTime(item.expires_at)}</span>;
}

function KeyRow({ item, canRotate, canRevoke, onRotate, onRevoke }) {
  const reason = asText(item.revoke_reason);
  return (
    <tr className="align-top" data-testid={`api-key-row-${item.id}`}>
      <td className="py-3 pr-4 text-sm font-medium text-gray-900 break-words max-w-[12rem]">
        {asText(item.name)}
      </td>
      <td className="py-3 pr-4">
        <StatusChip status={item.status} />
        {item.status === "revoked" && item.revoked_at ? (
          <div className="text-xs text-gray-500 mt-1">
            {formatDateTime(item.revoked_at)}
          </div>
        ) : null}
        {reason && (item.status === "revoked" || item.status === "retiring") ? (
          <div className="text-xs text-gray-500 mt-1 break-words max-w-[12rem]">
            {reason}
          </div>
        ) : null}
      </td>
      <td className="py-3 pr-4 text-xs text-gray-700 whitespace-nowrap">
        <ExpiryCell item={item} />
      </td>
      <td className="py-3 pr-4 text-xs text-gray-700 whitespace-nowrap">
        {item.last_used_at ? (
          formatDateTime(item.last_used_at)
        ) : (
          <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
            Never used
          </span>
        )}
      </td>
      <td className="py-3 pr-4 text-xs text-gray-700 whitespace-nowrap">
        {formatDateTime(item.created_at)}
      </td>
      {canRotate || canRevoke ? (
        <td className="py-3 text-right whitespace-nowrap">
          <div className="inline-flex items-center gap-1">
            {canRotate && item.status === "active" ? (
              <button
                type="button"
                onClick={() => onRotate(item)}
                aria-label={`Rotate ${keyLabel(item)}`}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-blue-600 hover:bg-blue-50 text-xs font-medium"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Rotate
              </button>
            ) : null}
            {canRevoke && isLive(item) ? (
              <button
                type="button"
                onClick={() => onRevoke(item)}
                aria-label={`Revoke ${keyLabel(item)}`}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-red-600 hover:bg-red-50 text-xs font-medium"
              >
                <Trash2 className="w-3.5 h-3.5" /> Revoke
              </button>
            ) : null}
          </div>
        </td>
      ) : null}
    </tr>
  );
}

function KeyList({ items, canRotate, canRevoke, onRotate, onRevoke }) {
  if (items.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-8 text-center border border-dashed border-gray-200 rounded-lg">
        No API keys yet.
      </div>
    );
  }
  const showActions = canRotate || canRevoke;
  return (
    <div
      id={API_KEY_LIST_ID}
      tabIndex={-1}
      aria-label="API keys"
      className="overflow-x-auto focus:outline-none"
    >
      <table className="w-full text-left">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
            <th className="py-2 pr-4 font-medium">Name</th>
            <th className="py-2 pr-4 font-medium">Status</th>
            <th className="py-2 pr-4 font-medium">Expires</th>
            <th className="py-2 pr-4 font-medium">Last used</th>
            <th className="py-2 pr-4 font-medium">Created</th>
            {showActions ? (
              <th className="py-2 font-medium text-right">
                <span className="sr-only">Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((item) => (
            <KeyRow
              key={item.id}
              item={item}
              canRotate={canRotate}
              canRevoke={canRevoke}
              onRotate={onRotate}
              onRevoke={onRevoke}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Create                                                              */
/* ------------------------------------------------------------------ */

function LimitsHint({ limits, liveCount }) {
  if (!limits) return null;
  const parts = [];
  if (Number.isFinite(limits.max_active_keys)) {
    parts.push(`${liveCount} of ${limits.max_active_keys} live keys in use.`);
  }
  if (
    Number.isFinite(limits.mints_per_window) &&
    Number.isFinite(limits.window_minutes)
  ) {
    parts.push(
      `Up to ${limits.mints_per_window} new keys every ${limits.window_minutes} minutes. Rotations are never blocked, but they count toward this limit.`
    );
  }
  if (parts.length === 0) return null;
  return <p className="text-xs text-gray-500">{parts.join(" ")}</p>;
}

function CreateKeyPanel({
  policy,
  items,
  limits,
  onCreated,
  onStale,
  onRotateExisting
}) {
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState(DEFAULT_EXPIRY);
  const [neverAck, setNeverAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [clash, setClash] = useState(null); // { message, conflict, body }
  const [result, setResult] = useState(null); // { data, lookup }

  const additive = policy === POLICY_ADDITIVE;
  const trimmed = name.trim();
  const needsAck = expiry === NEVER_EXPIRES && !neverAck;
  const canSubmit =
    trimmed.length > 0 &&
    trimmed.length <= MAX_NAME_LENGTH &&
    !needsAck &&
    !busy;

  // revoke_same_name (release N) revokes every not-yet-revoked key whose
  // stored name equals the submitted name exactly, expired ones included.
  // Keys whose name differs only in case or spacing are not matched by that
  // server, so they get a softer "may be revoked" warning instead.
  const doomed =
    !additive && trimmed
      ? items.filter(
          (item) => item.status !== "revoked" && item.name === trimmed
        )
      : [];
  const lookalikes =
    !additive && trimmed
      ? items.filter(
          (item) =>
            isLive(item) &&
            item.name !== trimmed &&
            sameName(item.name, trimmed)
        )
      : [];
  const buildBody = () =>
    additive
      ? { name: trimmed, expires_in_days: expiryToDays(expiry) }
      : { name: trimmed, expires_at: expiryToIso(expiry) };

  const send = async (body) => {
    setBusy(true);
    setError("");
    setClash(null);
    const lookup = buildKeyLookup(items);
    const res = await createApiKey(body);
    setBusy(false);
    if (res.success) {
      if (res.data?.raw_key) {
        setResult({ data: res.data, lookup });
        setName("");
        setExpiry(DEFAULT_EXPIRY);
        setNeverAck(false);
      } else {
        setError(
          "The server did not return the new key. Refresh the list and revoke it if it appears."
        );
      }
      onCreated?.(res.data);
      return;
    }
    if (additive && res.status === 409 && res.conflict) {
      setClash({ message: res.message, conflict: res.conflict });
      return;
    }
    setError(res.message);
    // Timeout, network error or 5xx: the key may have been created anyway.
    if (listMayBeStale(res)) onStale?.();
  };

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    send(buildBody());
  };

  // Built from the form as it is now (not as it was at the first submit), and
  // held to the same rules, e.g. the Never-expires acknowledgement.
  const createAnyway = () => {
    if (!canSubmit || !additive) return;
    send({ ...buildBody(), allow_duplicate_name: true });
  };

  if (result) {
    return (
      <NewKeyResult
        result={result.data}
        lookup={result.lookup}
        onDone={() => setResult(null)}
      />
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-gray-200 p-4 space-y-3"
      aria-label="Create API key"
    >
      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
        <Plus className="w-4 h-4 text-gray-500" /> Create a new key
      </h4>

      {!additive ? (
        <p className="flex items-start gap-2 text-xs text-gray-600">
          <Info className="w-4 h-4 shrink-0 text-gray-500" />
          <span>
            On this server, creating a key revokes any live key with the same
            name immediately.
          </span>
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label
            htmlFor="api-key-name"
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            Name
          </label>
          <input
            id="api-key-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setClash(null);
            }}
            maxLength={MAX_NAME_LENGTH}
            list="api-key-name-suggestions"
            placeholder="e.g. production"
            disabled={busy}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <datalist id="api-key-name-suggestions">
            {NAME_SUGGESTIONS.map((suggestion) => (
              <option key={suggestion} value={suggestion} />
            ))}
          </datalist>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
            <span>Suggestions:</span>
            {NAME_SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                disabled={busy}
                onClick={() => {
                  setName(suggestion);
                  setClash(null);
                }}
                className="px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 hover:bg-gray-100 font-mono"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>

        <ExpiryField
          id="api-key-expiry"
          value={expiry}
          onChange={(value) => {
            setExpiry(value);
            setNeverAck(false);
          }}
          ack={neverAck}
          onAckChange={setNeverAck}
          disabled={busy}
        />
      </div>

      {doomed.length > 0 || lookalikes.length > 0 ? (
        <div
          role="alert"
          className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="space-y-2">
            {doomed.length > 0 ? (
              <div>
                <p>
                  Creating this key will immediately revoke the existing “
                  {trimmed}” key. Anything using it will start getting 401
                  errors.
                </p>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {doomed.map((item) => (
                    <li key={item.id}>
                      Will be revoked: {keyLabel(item)} (
                      {item.status === "expired"
                        ? "already expired"
                        : lastUsedText(item)}
                      )
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {lookalikes.length > 0 ? (
              <div>
                <p>
                  A live key has a similar name that differs only in
                  capitalisation or spacing. This server may revoke it too.
                </p>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {lookalikes.map((item) => (
                    <li key={item.id}>
                      May be revoked: {keyLabel(item)} ({lastUsedText(item)})
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {clash ? (
        <ErrorBox message={clash.message}>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                const conflict = clash.conflict;
                setClash(null);
                onRotateExisting(conflict);
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Rotate existing key
            </button>
            <button
              type="button"
              disabled={!canSubmit}
              onClick={createAnyway}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5" /> Create with the same name anyway
            </button>
          </div>
        </ErrorBox>
      ) : null}

      <ErrorBox message={error} />

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        {additive ? (
          <LimitsHint limits={limits} liveCount={items.filter(isLive).length} />
        ) : (
          <span />
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Key className="w-4 h-4" />
          )}
          {doomed.length > 0 ? "Create and revoke existing key" : "Create key"}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                             */
/* ------------------------------------------------------------------ */

const normalizeList = (data) => ({
  mint_policy: data.mint_policy,
  can_manage: data.can_manage === true,
  limits: data.limits && typeof data.limits === "object" ? data.limits : null,
  items: data.items.filter((item) => item && typeof item === "object")
});

export default function ApiKeysSection() {
  // loading | list | legacy | forbidden | error
  const [phase, setPhase] = useState("loading");
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  // Read by refresh(), which can run long after it was created (after a
  // pending create or rotate); the ref always holds the current toggle.
  const includeInactiveRef = useRef(false);
  const [notice, setNotice] = useState("");
  const [rotateTarget, setRotateTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const requestSeq = useRef(0);

  /**
   * GET /api-keys doubles as capability detection:
   * 200 → list mode, 404/405 → legacy two-slot mode, 403 → message only.
   * Returns the normalised list on success, otherwise null.
   */
  const load = useCallback(async (inactive, initial = false) => {
    const seq = ++requestSeq.current;
    if (initial) {
      setPhase("loading");
      setLoadError("");
    } else {
      setRefreshing(true);
    }
    const res = await listApiKeys({ includeInactive: inactive });
    if (seq !== requestSeq.current) return null; // superseded
    setRefreshing(false);

    if (res.success) {
      if (!res.data || !Array.isArray(res.data.items)) {
        const message =
          "The server returned an unexpected response for the API key list.";
        if (initial) {
          setLoadError(message);
          setPhase("error");
        } else {
          setRefreshError(message);
        }
        return null;
      }
      const normalized = normalizeList(res.data);
      setData(normalized);
      setRefreshError("");
      setPhase("list");
      return normalized;
    }

    if (initial && (res.status === 404 || res.status === 405)) {
      setPhase("legacy");
      return null;
    }
    if (initial && res.status === 403) {
      setLoadError(res.message);
      setPhase("forbidden");
      return null;
    }
    if (initial) {
      setLoadError(res.message);
      setPhase("error");
    } else {
      setRefreshError(res.message);
    }
    return null;
  }, []);

  useEffect(() => {
    load(false, true);
  }, [load]);

  const refresh = () => load(includeInactiveRef.current);

  // Show what the server just reported on the rows already on screen, before the
  // reload lands: otherwise a key that was revoked a moment ago keeps showing
  // "Active" under the "Revoked" notice until the list round-trip completes.
  const patchItems = (update) =>
    setData((prev) =>
      prev ? { ...prev, items: prev.items.map((k) => update(k) || k) } : prev
    );

  const applyEffects = (result) => {
    if (!result || typeof result !== "object") return;
    const revoked = new Set((result.revoked_key_ids || []).map(String));
    const retiring = new Map(
      (Array.isArray(result.retiring) ? result.retiring : [])
        .filter((r) => r && r.id)
        .map((r) => [String(r.id), r])
    );
    if (!revoked.size && !retiring.size) return;
    patchItems((k) => {
      const id = String(k.id);
      if (revoked.has(id)) return { ...k, status: "revoked", is_active: false };
      if (retiring.has(id)) {
        return {
          ...k,
          status: "retiring",
          expires_at: retiring.get(id).retires_at || k.expires_at
        };
      }
      return null;
    });
  };

  const afterMutation = (result) => {
    applyEffects(result);
    refresh();
  };

  const setShowInactive = (checked) => {
    includeInactiveRef.current = checked;
    setIncludeInactive(checked);
  };

  const toggleInactive = (checked) => {
    setShowInactive(checked);
    load(checked);
  };

  const openRotateForConflict = async (conflict) => {
    let item = data?.items.find((k) => k.id === conflict.key_id);
    if (!item) {
      const fresh = await load(includeInactiveRef.current);
      item = fresh?.items.find((k) => k.id === conflict.key_id);
    }
    setRotateTarget(
      item || {
        id: conflict.key_id,
        name: asText(conflict.name),
        created_at: conflict.created_at,
        status: "active"
      }
    );
  };

  const handleRevoked = (revoked, target) => {
    // Close the dialog only if it is still the one for this key.
    setRevokeTarget((current) =>
      current && target && current.id !== target.id ? current : null
    );
    const label = keyLabel(revoked && revoked.id ? revoked : target);
    setNotice(
      revoked?.status === "revoked"
        ? `Revoked: ${label}`
        : `Revoke request sent for ${label}. Check the list for its current status.`
    );
    if (revoked?.status === "revoked" && revoked.id) {
      patchItems((k) =>
        String(k.id) === String(revoked.id)
          ? {
              ...k,
              status: "revoked",
              is_active: false,
              revoked_at: revoked.revoked_at ?? k.revoked_at,
              revoke_reason: revoked.revoke_reason ?? k.revoke_reason
            }
          : null
      );
    }
    // Show revoked keys so the result is visible in the list.
    setShowInactive(true);
    load(true);
  };

  let body;
  if (phase === "loading") {
    body = <LoadingState message="Loading API keys..." size="small" />;
  } else if (phase === "legacy") {
    body = <LegacyApiKeySlots />;
  } else if (phase === "forbidden") {
    body = (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
        <ShieldOff className="w-7 h-7 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-700">{loadError}</p>
      </div>
    );
  } else if (phase === "error") {
    body = (
      <ErrorBox message={loadError}>
        <button
          type="button"
          onClick={() => load(includeInactiveRef.current, true)}
          className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Retry
        </button>
      </ErrorBox>
    );
  } else {
    const policyKnown = KNOWN_POLICIES.includes(data.mint_policy);
    const canManage = data.can_manage;
    const canRotate =
      canManage && data.mint_policy === POLICY_ADDITIVE && policyKnown;
    body = (
      <div className="space-y-4">
        {notice ? (
          <div
            role="status"
            className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800"
          >
            <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span className="flex-1 break-all">{notice}</span>
            <button
              type="button"
              onClick={() => setNotice("")}
              className="text-xs text-green-700 hover:underline"
            >
              Dismiss
            </button>
          </div>
        ) : null}

        {!canManage ? (
          <p className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
            <Info className="w-4 h-4 shrink-0 text-gray-500" />
            <span>
              Only organization admins can create, rotate or revoke keys.
            </span>
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => toggleInactive(e.target.checked)}
            />
            Show revoked keys
          </label>
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>

        <ErrorBox message={refreshError} />

        <KeyList
          items={data.items}
          canRotate={canRotate}
          canRevoke={canManage}
          onRotate={setRotateTarget}
          onRevoke={setRevokeTarget}
        />

        {canManage && policyKnown ? (
          <CreateKeyPanel
            policy={data.mint_policy}
            items={data.items}
            limits={data.limits}
            onCreated={afterMutation}
            onStale={refresh}
            onRotateExisting={openRotateForConflict}
          />
        ) : null}

        {canManage && !policyKnown ? (
          <p className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
            <Info className="w-4 h-4 shrink-0 text-gray-500" />
            <span>
              This version of Orbit doesn't recognise the server's key policy (“
              {asText(data.mint_policy)}”), so creating keys is disabled. Reload
              the page to get the latest version.
            </span>
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <SettingsSection
      icon={Key}
      title="API Keys"
      description="Manage your organization's Orbit API keys"
    >
      {body}
      {rotateTarget ? (
        <RotateKeyDialog
          keyItem={rotateTarget}
          lookup={buildKeyLookup(data?.items)}
          onClose={() => setRotateTarget(null)}
          onRotated={afterMutation}
          onStale={refresh}
        />
      ) : null}
      {revokeTarget ? (
        <RevokeKeyDialog
          keyItem={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onRevoked={handleRevoked}
          onStale={refresh}
        />
      ) : null}
    </SettingsSection>
  );
}
